import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMenu, FiX, FiChevronDown } from 'react-icons/fi';
import Logo from './Logo';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsSolutionsOpen(false);
  };

  const toggleSolutions = () => {
    setIsSolutionsOpen(!isSolutionsOpen);
  };

  const handleNavHover = (navItem) => {
    setHoveredNavItem(navItem);
    if (navItem !== 'solutions') {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setIsSolutionsOpen(false);
    }
  };

  const handleNavLeave = () => {
    setHoveredNavItem(null);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    // Don't close solutions dropdown immediately when leaving other nav items
    // Let the solutions dropdown handle its own timeout
  };

  const handleSolutionsMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsSolutionsOpen(true);
  };

  const handleSolutionsMouseLeave = (e) => {
    // Only close if the mouse is leaving the entire dropdown container
    // and not moving to a child element
    if (e.relatedTarget && dropdownRef.current && 
        e.relatedTarget instanceof Node && 
        !dropdownRef.current.contains(e.relatedTarget)) {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsSolutionsOpen(false);
      }, 200);
    } else if (!e.relatedTarget || !(e.relatedTarget instanceof Node)) {
      // If relatedTarget is null or not a Node, close the dropdown
      hoverTimeoutRef.current = setTimeout(() => {
        setIsSolutionsOpen(false);
      }, 200);
    }
  };


  const isHovered = (navItem) => {
    return hoveredNavItem === navItem;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsSolutionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMenuOpen(false);
        setIsSolutionsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle body scroll lock when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }

    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMenuOpen]);

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          {/* Logo */}
          <Logo />

          {/* Desktop Navigation */}
          <nav 
            className="nav-desktop"
            onMouseLeave={() => {
              if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
              }
              hoverTimeoutRef.current = setTimeout(() => {
                setIsSolutionsOpen(false);
                setHoveredNavItem(null);
              }, 300);
            }}
          >
            <div 
              className="nav-dropdown"
              ref={dropdownRef}
              onMouseEnter={handleSolutionsMouseEnter}
              onMouseLeave={handleSolutionsMouseLeave}
            >
              <button 
                className={`dropdown-trigger ${isSolutionsOpen ? 'dropdown-open' : ''} ${isHovered('solutions') ? 'nav-hovered' : ''}`}
                onClick={toggleSolutions}
              >
                Products
                <FiChevronDown className={`dropdown-icon ${isSolutionsOpen ? 'open' : ''}`} />
              </button>
              
              {isSolutionsOpen && (
                <motion.div 
                  className="dropdown-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  onMouseEnter={handleSolutionsMouseEnter}
                  onMouseLeave={handleSolutionsMouseLeave}
                >
                  <div className="dropdown-section">
                    <h3 className="dropdown-section-header">OBSERVABILITY & MONITORING</h3>
                    <Link to="/observability-platform" className="dropdown-item" onClick={closeMenu}>
                      Observability Platform
                    </Link>
                    <Link to="/log-management" className="dropdown-item" onClick={closeMenu}>
                      Log Management
                    </Link>
                    <Link to="/apm" className="dropdown-item" onClick={closeMenu}>
                      APM
                    </Link>
                    <Link to="/infrastructure-monitoring" className="dropdown-item" onClick={closeMenu}>
                      Infrastructure Monitoring
                    </Link>
                    <Link to="/cloud-monitoring" className="dropdown-item" onClick={closeMenu}>
                      Cloud Monitoring
                    </Link>
                    <Link to="/ai-monitoring" className="dropdown-item" onClick={closeMenu}>
                      AI Monitoring
                    </Link>
                    <Link to="/devops-observability" className="dropdown-item" onClick={closeMenu}>
                      DevOps Observability
                    </Link>
                  </div>
                  
                  <div className="dropdown-section">
                    <h3 className="dropdown-section-header">AGENTSRE SUITE</h3>
                    <Link to="/agent-sre" className="dropdown-item" onClick={closeMenu}>
                      AgentSRE
                    </Link>
                    <Link to="/ai-alerting" className="dropdown-item" onClick={closeMenu}>
                      AI Alerting
                    </Link>
                    <Link to="/security" className="dropdown-item" onClick={closeMenu}>
                      Security
                    </Link>
                    <Link to="/ai-ops" className="dropdown-item" onClick={closeMenu}>
                      AIOps
                    </Link>
                  </div>
                  
                  <div className="dropdown-section">
                    <h3 className="dropdown-section-header">ADVANCED AI OBSERVABILITY</h3>
                    <Link to="/advanced-aiops" className="dropdown-item" onClick={closeMenu}>
                      Advanced AIOps
                    </Link>
                    <Link to="/rca-agent" className="dropdown-item" onClick={closeMenu}>
                      RCA Agent
                    </Link>
                    <Link to="/b2b-success-agent" className="dropdown-item" onClick={closeMenu}>
                      B2B Success Agent
                    </Link>
                    <Link to="/agentic-database" className="dropdown-item" onClick={closeMenu}>
                      Agentic Database
                    </Link>
                  </div>
                </motion.div>
              )}
            </div>
            
            <Link 
              to="/pricing" 
              className={`nav-link ${isHovered('pricing') ? 'nav-hovered' : ''}`}
              onMouseEnter={() => handleNavHover('pricing')}
              onMouseLeave={handleNavLeave}
            >
              Pricing
            </Link>
            
            <Link 
              to="/contact" 
              className={`nav-link ${isHovered('contact') ? 'nav-hovered' : ''}`}
              onMouseEnter={() => handleNavHover('contact')}
              onMouseLeave={handleNavLeave}
            >
              Contact
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div 
            className="header-actions"
            onMouseEnter={() => {
              // Close dropdown when hovering over header actions
              if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
              }
              setIsSolutionsOpen(false);
            }}
          >
            <Link to="/demo" className="btn btn-outline">
              Request Demo
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="nav-mobile">
            <div className="mobile-dropdown">
              <div className="mobile-dropdown-menu">
                  <div className="mobile-dropdown-section">
                    <h3 className="mobile-section-header">OBSERVABILITY & MONITORING</h3>
                    <Link to="/observability-platform" className="mobile-dropdown-item" onClick={closeMenu}>
                      Observability Platform
                    </Link>
                    <Link to="/log-management" className="mobile-dropdown-item" onClick={closeMenu}>
                      Log Management
                    </Link>
                    <Link to="/apm" className="mobile-dropdown-item" onClick={closeMenu}>
                      APM
                    </Link>
                    <Link to="/infrastructure-monitoring" className="mobile-dropdown-item" onClick={closeMenu}>
                      Infrastructure Monitoring
                    </Link>
                    <Link to="/cloud-monitoring" className="mobile-dropdown-item" onClick={closeMenu}>
                      Cloud Monitoring
                    </Link>
                    <Link to="/ai-monitoring" className="mobile-dropdown-item" onClick={closeMenu}>
                      AI Monitoring
                    </Link>
                    <Link to="/devops-observability" className="mobile-dropdown-item" onClick={closeMenu}>
                      DevOps Observability
                    </Link>
                  </div>
                  
                  <div className="mobile-dropdown-section">
                    <h3 className="mobile-section-header">AGENTSRE SUITE</h3>
                    <Link to="/agent-sre" className="mobile-dropdown-item" onClick={closeMenu}>
                      AgentSRE
                    </Link>
                    <Link to="/ai-alerting" className="mobile-dropdown-item" onClick={closeMenu}>
                      AI Alerting
                    </Link>
                    <Link to="/security" className="mobile-dropdown-item" onClick={closeMenu}>
                      Security
                    </Link>
                    <Link to="/ai-ops" className="mobile-dropdown-item" onClick={closeMenu}>
                      AIOps
                    </Link>
                  </div>
                  
                  <div className="mobile-dropdown-section">
                    <h3 className="mobile-section-header">ADVANCED AI OBSERVABILITY</h3>
                    <Link to="/advanced-aiops" className="mobile-dropdown-item" onClick={closeMenu}>
                      Advanced AIOps
                    </Link>
                    <Link to="/rca-agent" className="mobile-dropdown-item" onClick={closeMenu}>
                      RCA Agent
                    </Link>
                    <Link to="/b2b-success-agent" className="mobile-dropdown-item" onClick={closeMenu}>
                      B2B Success Agent
                    </Link>
                    <Link to="/agentic-database" className="mobile-dropdown-item" onClick={closeMenu}>
                      Agentic Database
                    </Link>
                  </div>
                </div>
              </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;